import React from 'react';
import useVideoScrub from '../hooks/useVideoScrub';
import QuizQuestion from './QuizQuestion';
import { QUESTIONS, DYNAMIC_VIDEOS } from '../data/quizConfig';
import {
  PageContainer,
  VideoContainer,
  Video,
  IntroHeadline,
  ScrollHint,
  ScrollNavBar,
  ScrollTrack,
  ScrollProgressBar,
  Marker,
  MarkerTooltip,
  QuizOverlay,
  QuizContainer,
} from '../styles/MFLancamentosStyles';
import styled, { keyframes } from 'styled-components';

const nudgeFade = keyframes`
  0% { opacity: 0; transform: translateX(-50%) translateY(8px); }
  15% { opacity: 1; transform: translateX(-50%) translateY(0); }
  85% { opacity: 1; transform: translateX(-50%) translateY(0); }
  100% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
`;

const NudgeMessage = styled.div`
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  padding: 12px 28px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  font-weight: 300;
  letter-spacing: 0.3px;
  z-index: 300;
  backdrop-filter: blur(12px);
  animation: ${nudgeFade} 2.5s ease forwards;
  pointer-events: none;
`;

const VideoScrubSection = ({ onQuizComplete, onAnswerCollected }) => {
  const {
    video1Ref,
    video2Ref,
    video3Ref,
    introVideoRef,
    scrollTrackRef,
    scrollProgress,
    isLoaded,
    hasStartedScrolling,
    introVideoLoaded,
    setIntroVideoLoaded,
    currentVideoIndex,
    previousVideoIndex,
    scrollHeight,
    activeQuizQuestion,
    showNudge,
    quizCompleted,
    unlockAndAdvance,
    handleLoadedMetadata,
    handleCanPlayThrough,
    handleMarkerClick,
    handleTrackMouseDown,
    handleTrackTouchStart,
    handleScreenClick,
    getMarkerPositions,
    VIDEOS,
    swapVideoSource,
  } = useVideoScrub();

  const markerPositions = getMarkerPositions();

  // Estado para controlar animação de saída do quiz
  // 'hidden' | 'visible' | 'exiting'
  const [quizPhase, setQuizPhase] = React.useState('hidden');
  const isQuizActive = activeQuizQuestion >= 0 && activeQuizQuestion < QUESTIONS.length;

  // Quando quiz fica ativo, mostra. Quando desativa, inicia saída.
  React.useEffect(() => {
    if (isQuizActive) {
      setQuizPhase('visible');
    } else if (quizPhase === 'visible') {
      setQuizPhase('exiting');
      const timer = setTimeout(() => setQuizPhase('hidden'), 400);
      return () => clearTimeout(timer);
    }
  }, [isQuizActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vídeo final: toca após quiz 2 (tamanho) antes de mostrar resultados
  const [showFinalVideo, setShowFinalVideo] = React.useState(false);
  const finalVideoRef = React.useRef(null);

  // Quando quiz completa, mostra vídeo final em vez de ir direto pros resultados
  React.useEffect(() => {
    if (quizCompleted) {
      setShowFinalVideo(true);
    }
  }, [quizCompleted]);

  const handleFinalVideoEnd = () => {
    // NÃO remove o vídeo — mantém último frame visível até a tela de resultados substituir
    if (onQuizComplete) {
      onQuizComplete();
    }
  };

  // Armazena a resposta de localização (quiz 0) para usar no swap do quiz 1
  const locationAnswerRef = React.useRef(null);

  // Handler quando o usuário seleciona uma opção do quiz
  const handleQuizSelect = (questionId, value) => {
    if (onAnswerCollected) {
      onAnswerCollected(questionId, value);
    }

    const questionIndex = activeQuizQuestion;

    // Quiz 0 (localização): armazena resposta e troca vídeo da transição 2
    if (questionIndex === 0) {
      locationAnswerRef.current = value;
    }

    // Troca o vídeo da próxima transição
    const videoMap = DYNAMIC_VIDEOS[questionIndex];
    // Quiz 1: usa localização (quiz 0) para lookup, não o perfil
    const lookupValue = questionIndex === 1 ? locationAnswerRef.current : value;
    if (videoMap && lookupValue && videoMap[lookupValue]) {
      swapVideoSource(questionIndex + 1, videoMap[lookupValue]);
    }

    // Espera a animação de saída antes de destrancar
    setTimeout(() => {
      unlockAndAdvance();
    }, 400);
  };

  const isQuizVisible = quizPhase !== 'hidden';

  return (
    <PageContainer $scrollHeight={scrollHeight || window.innerHeight * 2}>
      {/* Vídeo de intro em loop - aparece antes do scroll */}
      {!hasStartedScrolling && (
        <VideoContainer style={{ zIndex: 2 }}>
          <Video
            ref={introVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setIntroVideoLoaded(true)}
          >
            <source src="/mf-lancamentos/video_intro.mp4" type="video/mp4" />
          </Video>
        </VideoContainer>
      )}

      {/* Vídeos de transição controlados por scroll — sistema de camadas */}
      {/* Vídeo 1 */}
      <VideoContainer style={{
        zIndex: currentVideoIndex === 0 ? 3 : (previousVideoIndex === 0 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video1Ref}
          onLoadedMetadata={() => handleLoadedMetadata(0)}
          onCanPlayThrough={() => handleCanPlayThrough(0)}
          onCanPlay={() => handleCanPlayThrough(0)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{
            opacity: hasStartedScrolling && (currentVideoIndex === 0 || previousVideoIndex === 0) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src={VIDEOS[0]} type="video/mp4" />
        </Video>
      </VideoContainer>

      {/* Vídeo 2 */}
      <VideoContainer style={{
        zIndex: currentVideoIndex === 1 ? 3 : (previousVideoIndex === 1 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video2Ref}
          onLoadedMetadata={() => handleLoadedMetadata(1)}
          onCanPlayThrough={() => handleCanPlayThrough(1)}
          onCanPlay={() => handleCanPlayThrough(1)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{
            opacity: hasStartedScrolling && (currentVideoIndex === 1 || previousVideoIndex === 1) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src={VIDEOS[1]} type="video/mp4" />
        </Video>
      </VideoContainer>

      {/* Vídeo 3 */}
      <VideoContainer style={{
        zIndex: currentVideoIndex === 2 ? 3 : (previousVideoIndex === 2 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video3Ref}
          onLoadedMetadata={() => handleLoadedMetadata(2)}
          onCanPlayThrough={() => handleCanPlayThrough(2)}
          onCanPlay={() => handleCanPlayThrough(2)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{
            opacity: hasStartedScrolling && (currentVideoIndex === 2 || previousVideoIndex === 2) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src={VIDEOS[2]} type="video/mp4" />
        </Video>
      </VideoContainer>

      {/* Headline e indicador de scroll no início */}
      {!hasStartedScrolling && introVideoLoaded && (
        <>
          <IntroHeadline>
            Descubra uma <em>nova forma</em> de comprar seu imóvel
          </IntroHeadline>
          <ScrollHint />
        </>
      )}

      {/* Barra de navegação lateral */}
      <ScrollNavBar $visible={hasStartedScrolling && isLoaded}>
        <ScrollTrack
          ref={scrollTrackRef}
          onMouseDown={handleTrackMouseDown}
          onTouchStart={handleTrackTouchStart}
        >
          <ScrollProgressBar $progress={scrollProgress * 100} />
          {markerPositions.map((position, index) => {
            const isLastMarker = index === markerPositions.length - 1;
            const isActive = isLastMarker
              ? scrollProgress >= 0.98
              : currentVideoIndex === index;
            const label = isLastMarker ? 'Final' : `Transição ${index + 1}`;

            return (
              <Marker
                key={index}
                $position={Math.min(position, 97)}
                $active={isActive}
                onClick={() => handleMarkerClick(index)}
                aria-label={`Ir para ${label}`}
              >
                <MarkerTooltip>{label}</MarkerTooltip>
              </Marker>
            );
          })}
        </ScrollTrack>
      </ScrollNavBar>

      {/* Quiz overlay — aparece sobre o vídeo quando transição termina */}
      {isQuizVisible && (
        <QuizOverlay style={quizPhase === 'exiting' ? { opacity: 0, transition: 'opacity 400ms ease' } : undefined}>
          <QuizContainer className={quizPhase === 'exiting' ? 'exit-left' : 'enter-right'}>
            {isQuizActive && (
              <QuizQuestion
                question={QUESTIONS[activeQuizQuestion]}
                currentStep={activeQuizQuestion}
                totalSteps={QUESTIONS.length}
                onSelect={handleQuizSelect}
              />
            )}
          </QuizContainer>
        </QuizOverlay>
      )}

      {/* Vídeo final — toca após última pergunta, antes dos resultados */}
      {showFinalVideo && (
        <VideoContainer style={{ zIndex: 250 }}>
          <Video
            ref={finalVideoRef}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={handleFinalVideoEnd}
            style={{ opacity: 1 }}
          >
            <source src="/mf-lancamentos/transicao_final.mp4" type="video/mp4" />
          </Video>
        </VideoContainer>
      )}

      {/* Nudge message — quando tenta scrollar sem responder */}
      {showNudge && (
        <NudgeMessage>
          Responda para continuar
        </NudgeMessage>
      )}
    </PageContainer>
  );
};

export default VideoScrubSection;
